import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"

// types
import { SliceName } from "./slice.types"
import {
  EnrichedUserData,
  EventbriteEvent,
  EventbriteMediaResponseData,
  EventbriteOrder,
  CreatorTierData,
  CreatorTier
} from "../../utils/auth/enrich_user_data"
import { EventbriteUserResponse } from "../../components/ProfileMenuBar/ProfileMenuBar"
import { AssortmentPlan } from "../../types/api"
import { UserSignatureResponseData } from "../../services/chatbot_linking.service"

export type AuthResponse = {
  language: string
  valid: boolean
  uid: number
  oid: string
  email: string
}

export type UserProfile = {
  allData: EnrichedUserData
  userData: EventbriteUserResponse
  assortmentPlan: AssortmentPlan | undefined
  email: string | undefined
  organizationId: string
  isHighValue: boolean
  isSalesCreator: boolean
  isPaidCreator: boolean
  // isFree and isFreeCreator are identical variables
  isFreeCreator: boolean
  isFree: boolean
  isOrganizer: boolean
  isNewAccount: boolean
  isAttendee: boolean
  avatar?: EventbriteMediaResponseData | Record<string, never>
  name?: string
  orders?: EventbriteOrder[]
  events?: EventbriteEvent[]
  creatorTierData?: CreatorTierData
  creatorTier?: CreatorTier
  userSignatureResponseData?: UserSignatureResponseData
}

// Cast widens the literal null so createSlice infers state as
// UserProfile | null instead of just null (which would make all
// downstream userProfile accesses resolve to `never`).
const initialState = null as UserProfile | null

export const userProfileSlice = createSlice({
  name: SliceName.userProfile,
  initialState,
  reducers: {
    setUserProfile: (s, action: PayloadAction<Partial<UserProfile>>) => {
      const guardedUserProfile = userProfileGuardCheck(s)
      const finalState = {
        ...guardedUserProfile,
        ...action.payload
      } as UserProfile

      return finalState
    },
    logout: () => {
      return null
    }
  }
})

// Handle checking that the basic shape of the argument is a UserProfile
export const userProfileGuardCheck = (
  val: unknown
): UserProfile | Record<string, never> => {
  if (val !== null && typeof val === "object" && "organizationId" in val) {
    return val as UserProfile
  }

  return {}
}

// Action creators are generated for each case reducer function
export const { setUserProfile, logout } = userProfileSlice.actions

export const userProfileReducer = userProfileSlice.reducer
